
import React, { useState, useEffect } from 'react';
import { Lock, ShieldCheck, Zap, Gift, CheckCircle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import GoogleSignIn from './GoogleSignIn';
import { authService } from '../services/authService';

interface VaultEntryProps {
  onAuthenticate: () => void;
  onClose?: () => void;
}

const VaultEntry: React.FC<VaultEntryProps> = ({ onAuthenticate, onClose }) => {
  const [glitch, setGlitch] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponMessage, setCouponMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isRedeeming, setIsRedeeming] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 150);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleSignInSuccess = async (userId: string) => {
    console.log('✅ Authentication successful:', userId);

    // Small delay for smooth transition
    setTimeout(() => {
      onAuthenticate();
    }, 500);
  };

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) return;

    setIsRedeeming(true);
    setCouponMessage(null);

    try {
      const userId = sessionStorage.getItem('ielts_user_id');

      // If user is not logged in, try to login with the code (Dev Backdoor)
      if (!userId) {
        const loginResult = await authService.loginWithDevCode(couponCode.trim());
        if (loginResult.success && loginResult.userId) {
          setCouponMessage({ type: 'success', text: loginResult.message });
          setCouponCode('');

          // Wait a bit then authenticate using the dev user id
          setTimeout(() => {
            setShowCouponModal(false);
            handleSignInSuccess(loginResult.userId!);
          }, 1500);
        } else {
          // Not a dev code, and user not logged in
          setCouponMessage({ type: 'error', text: 'Please sign in first for standard coupons.' });
        }
        setIsRedeeming(false);
        return;
      }

      // If user is logged in, use standard redeem
      const result = await authService.redeemCoupon(userId, couponCode.trim());

      if (result.success) {
        setCouponMessage({ type: 'success', text: result.message });
        setCouponCode('');

        // Close modal and refresh after success
        setTimeout(() => {
          setShowCouponModal(false);
          // Just close modal, the app will update state automatically via listeners or reload
          window.dispatchEvent(new CustomEvent('user-stats-updated', { detail: { tier: result.tier } }));
        }, 2000);
      } else {
        setCouponMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      console.error(error);
      setCouponMessage({ type: 'error', text: 'Failed to redeem coupon' });
    } finally {
      setIsRedeeming(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0f0f0f] flex items-center justify-center p-6 z-50">
      <div className="scanline opacity-20"></div>

      <div className={`max-w-md w-full transition-all duration-500 ${glitch ? 'skew-x-2' : ''}`}>
        <div className="bg-[#1c1917] border-2 border-[#C5A059]/30 rounded-lg p-8 shadow-2xl relative overflow-hidden">
          {/* Close Button for Guest Mode */}
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-[#C5A059]/40 hover:text-[#C5A059] transition-colors z-20"
            >
              <X size={20} />
            </button>
          )}

          {/* Cyber accents */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C5A059]/40 to-transparent"></div>
          <div className="absolute top-0 left-0 p-2 text-[8px] mono text-[#C5A059]/20">0x7492-SECURE</div>

          <div className="flex flex-col items-center mb-10">
            <div className="w-24 h-24 rounded-full border-2 border-[#C5A059] flex items-center justify-center mb-6 gold-glow">
              <Lock size={40} className={`text-[#C5A059] ${glitch ? 'opacity-50' : ''}`} />
            </div>

            <h1 className="text-3xl font-black text-[#C5A059] tracking-[0.25em] mono uppercase relative text-center">
              TED-TALKS-DZ
              {glitch && <span className="absolute inset-0 text-red-500/30 -translate-x-1">TED-TALKS-DZ</span>}
            </h1>
            <p className="text-[#C5A059]/60 text-[10px] mt-3 mono tracking-widest flex items-center gap-2">
              <Zap size={10} className="animate-pulse" /> VISUAL WARFARE SYSTEM V2.5
            </p>
          </div>

          {/* Google Sign-In */}
          <GoogleSignIn onSuccess={handleSignInSuccess} />

          {/* Coupon Code Button */}
          <button
            onClick={() => setShowCouponModal(true)}
            className="w-full mt-6 py-3 border border-[#C5A059]/20 text-[#C5A059]/60 hover:text-[#C5A059] hover:border-[#C5A059]/40 rounded transition-all flex items-center justify-center gap-2 text-sm mono tracking-wide"
          >
            <Gift size={16} /> Have a coupon code?
          </button>

          <div className="mt-10 pt-6 border-t border-[#C5A059]/5 text-[9px] text-[#C5A059]/30 mono flex justify-between items-center uppercase">
            <span className="flex items-center gap-1">
              <div className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"></div>
              Secure Auth
            </span>
            <span>Uptime: 99.982%</span>
          </div>
        </div>
      </div>

      {/* Coupon Modal */}
      <AnimatePresence>
        {showCouponModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6"
            onClick={() => setShowCouponModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#1c1917] border-2 border-[#C5A059]/30 rounded-2xl p-8 max-w-md w-full relative"
            >
              <button
                onClick={() => setShowCouponModal(false)}
                className="absolute top-4 right-4 p-2 text-stone-500 hover:text-[#C5A059] transition-colors"
              >
                <X size={20} />
              </button>

              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-[#C5A059]/20 border-2 border-[#C5A059] flex items-center justify-center mx-auto mb-4">
                  <Gift className="text-[#C5A059]" size={32} />
                </div>
                <h3 className="text-2xl font-black text-[#C5A059] mb-2 tracking-tight">Redeem Coupon</h3>
                <p className="text-stone-500 text-sm">Enter your special access code</p>
              </div>

              <div className="space-y-4">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && handleRedeemCoupon()}
                  placeholder="ENTER-CODE-HERE"
                  className="w-full bg-black/40 border border-[#C5A059]/20 text-[#C5A059] rounded px-4 py-4 outline-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/20 transition-all mono text-center tracking-[0.3em] font-bold text-lg"
                  disabled={isRedeeming}
                />

                {couponMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 rounded-lg flex items-center gap-3 ${couponMessage.type === 'success'
                        ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                        : 'bg-red-500/10 border border-red-500/20 text-red-400'
                      }`}
                  >
                    {couponMessage.type === 'success' ? (
                      <CheckCircle size={20} />
                    ) : (
                      <X size={20} />
                    )}
                    <p className="text-sm font-bold">{couponMessage.text}</p>
                  </motion.div>
                )}

                <button
                  onClick={handleRedeemCoupon}
                  disabled={isRedeeming || !couponCode.trim()}
                  className="w-full py-4 bg-[#C5A059] text-stone-900 font-black rounded-xl mono text-sm hover:bg-[#B48F48] disabled:opacity-50 disabled:cursor-not-allowed transition-all tracking-wide"
                >
                  {isRedeeming ? 'VALIDATING...' : 'REDEEM CODE'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VaultEntry;
